import React, { useState, useEffect, useRef } from 'react';
import {
    TextInput,
    Modal,
    Box,
    Text,
    Stack,
    Group,
    Badge,
    ActionIcon,
    Loader,
    ScrollArea,
    Paper,
    Divider,
    UnstyledButton,
    Button,
} from '@mantine/core';
import { IconSearch, IconX, IconInvoice, IconSchool, IconUsers, IconClipboardList, IconUserCheck } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';

interface SearchResult {
    invoices: any[];
    batches: any[];
    students: any[];
    instructors: any[];
}

interface GlobalSearchProps {
    apiBaseUrl?: string;
}

type FilterType = 'all' | 'invoice' | 'batch' | 'student' | 'instructor';

export default function GlobalSearch({ apiBaseUrl = 'https://admin-dashboard-backend-sbu4.onrender.com' }: GlobalSearchProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterType>('all');
    const [searchResults, setSearchResults] = useState<SearchResult>({
        invoices: [],
        batches: [],
        students: [],
        instructors: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (debouncedQuery.trim().length > 0) {
            performSearch(debouncedQuery);
        } else {
            setSearchResults({
                invoices: [],
                batches: [],
                students: [],
                instructors: [],
            });
        }
    }, [debouncedQuery]);

    const performSearch = async (query: string) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${apiBaseUrl}/search?q=${encodeURIComponent(query)}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            } else {
                console.error('Search failed:', response.statusText);
            }
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
        setActiveFilter('all');
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSearchQuery('');
        setActiveFilter('all');
        setSearchResults({
            invoices: [],
            batches: [],
            students: [],
            instructors: [],
        });
    };

    const handleResultClick = (type: string) => {
    handleCloseModal();

    switch (type) {
        case 'invoice':
            navigate('/Invoice');
            break;
        case 'batch':
            navigate('/Batches');
            break;
        case 'student':
            navigate('/Students');
            break;
        case 'instructor':
            navigate('/Instructors');
            break;
    }
};

    const renderInvoiceCard = (invoice: any) => (
        <UnstyledButton
            key={invoice.inv_no}
            onClick={() => handleResultClick('invoice')}
            style={{ width: '100%' }}
        >
            <Paper
                p="md"
                withBorder
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                }}
                className="hover:shadow-md hover:border-blue-400"
            >
                <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                        <IconInvoice size={18} color="#ffc107" />
                        <Text fw={600} size="sm">Invoice #{invoice.inv_no}</Text>
                    </Group>
                    <Badge color={invoice.status === 'Payment Sent' ? 'green' : 'orange'} size="sm">
                        {invoice.status}
                    </Badge>
                </Group>
                <Stack gap="xs">
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Trainer:</Text>
                        <Text size="xs" fw={500}>{invoice.trainerName}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Course:</Text>
                        <Text size="xs" fw={500}>{invoice.courseName}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Amount:</Text>
                        <Text size="xs" fw={500}>₹{invoice.netPayable?.toLocaleString()}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Date:</Text>
                        <Text size="xs" fw={500}>{invoice.date}</Text>
                    </Group>
                </Stack>
            </Paper>
        </UnstyledButton>
    );

    const renderBatchCard = (batch: any) => (
        <UnstyledButton
            key={batch.batch_no}
            onClick={() => handleResultClick('batch')}
            style={{ width: '100%' }}
        >
            <Paper
                p="md"
                withBorder
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                }}
                className="hover:shadow-md hover:border-red-400"
            >
                <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                        <IconSchool size={18} color="#f5576c" />
                        <Text fw={600} size="sm">Batch #{batch.batch_no}</Text>
                    </Group>
                    <Badge color={batch.batchStatus === 'On-going' ? 'blue' : 'gray'} size="sm">
                        {batch.batchStatus}
                    </Badge>
                </Group>
                <Stack gap="xs">
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Course:</Text>
                        <Text size="xs" fw={500}>{batch.courseName}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Trainer:</Text>
                        <Text size="xs" fw={500}>{batch.trainerName}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Students:</Text>
                        <Text size="xs" fw={500}>{batch.students?.length || 0}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Start Date:</Text>
                        <Text size="xs" fw={500}>{batch.startDate}</Text>
                    </Group>
                </Stack>
            </Paper>
        </UnstyledButton>
    );

    const renderStudentCard = (student: any) => (
        <UnstyledButton
            key={student.student_id}
            onClick={() => handleResultClick('student')}
            style={{ width: '100%' }}
        >
            <Paper
                p="md"
                withBorder
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                }}
                className="hover:shadow-md hover:border-green-400"
            >
                <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                        <IconUsers size={18} color="#10b981" />
                        <Text fw={600} size="sm">{student.name}</Text>
                    </Group>
                    <Badge color={student.paymentStatus === 'Paid' ? 'green' : 'orange'} size="sm">
                        {student.paymentStatus}
                    </Badge>
                </Group>
                <Stack gap="xs">
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">ID:</Text>
                        <Text size="xs" fw={500}>{student.student_id}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Email:</Text>
                        <Text size="xs" fw={500}>{student.email}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Mobile:</Text>
                        <Text size="xs" fw={500}>{student.mobile}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Enrolled:</Text>
                        <Text size="xs" fw={500}>{student.enrollDate}</Text>
                    </Group>
                </Stack>
            </Paper>
        </UnstyledButton>
    );


    const renderInstructorCard = (instructor: any) => (
        <UnstyledButton
            key={instructor.Email}
            onClick={() => handleResultClick('instructor')}
            style={{ width: '100%' }}
        >
            <Paper
                p="md"
                withBorder
                style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                }}
                className="hover:shadow-md hover:border-purple-400"
            >
                <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                        <IconUserCheck size={18} color="#a855f7" />
                        <Text fw={600} size="sm">{instructor.Name}</Text>
                    </Group>
                    {instructor.tags && (
                        <Badge color="purple" size="sm">
                            {instructor.tags}
                        </Badge>
                    )}
                </Group>
                <Stack gap="xs">
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Email:</Text>
                        <Text size="xs" fw={500}>{instructor.Email}</Text>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">Contact:</Text>
                        <Text size="xs" fw={500}>{instructor.Contact}</Text>
                    </Group>
                    {instructor.sno && (
                        <Group gap="xs">
                            <Text size="xs" c="dimmed">S.No:</Text>
                            <Text size="xs" fw={500}>{instructor.sno}</Text>
                        </Group>
                    )}
                </Stack>
            </Paper>
        </UnstyledButton>
    );

    const renderSection = (title: string, icon: any, data: any[], renderCard: (item: any) => JSX.Element, color: string) => {
        if (searchQuery.trim() === '') return null;

        return (
            <Box>
                <Group gap="xs" mb="sm">
                    {icon}
                    <Text fw={600} size="md" c={color}>{title}</Text>
                    <Badge color={color} size="sm">{data.length}</Badge>
                </Group>
                
                {data.length === 0 ? (
                    <Paper p="md" withBorder style={{ backgroundColor: '#f9fafb' }}>
                        <Text size="sm" c="dimmed" ta="center">No results found</Text>
                    </Paper>
                ) : (
                    <Stack gap="sm">
                        {data.map(renderCard)}
                    </Stack>
                )}
            </Box>
        );
    };

    const shouldShowSection = (type: FilterType) => {
        return activeFilter === 'all' || activeFilter === type;
    };

    const getFilteredCount = (type: FilterType) => {
        switch (type) {
            case 'invoice': return searchResults.invoices.length;
            case 'batch': return searchResults.batches.length;
            case 'student': return searchResults.students.length;
            case 'instructor': return searchResults.instructors.length;
            default: return 0;
        }
    };

    const totalResults = searchResults.invoices.length + searchResults.batches.length + 
                         searchResults.students.length  +
                         searchResults.instructors.length;

    return (
        <>
            {/* Search Trigger Button */}
            {/* <UnstyledButton onClick={handleOpenModal}>
                <Paper
                    p="xs"
                    px="md"
                    withBorder
                    style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: '250px',
                    }}
                    className="hover:shadow-sm hover:border-blue-400"
                >
                    <Group gap="xs">
                        <IconSearch size={18} color="#9ca3af" />
                        <Text size="sm" c="dimmed">Search...</Text>
                    </Group>
                </Paper>
            </UnstyledButton> */}
<UnstyledButton onClick={handleOpenModal} className="hidden md:block">
  <Paper
    p="xs"
    px="md"
    withBorder
    style={{
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      width: '100%',
    }}
    className="hover:shadow-sm hover:border-blue-400"
  >
    <Group gap="xs">
      <IconSearch size={18} color="#9ca3af" />
      <Text size="sm" c="dimmed">Search...</Text>
    </Group>
  </Paper>
</UnstyledButton>

            {/* Search Modal */}
            <Modal
                opened={isModalOpen}
                onClose={handleCloseModal}
                size="xl"
                title={
                    <Group gap="xs">
                        <IconSearch size={20} />
                        <Text fw={600}>Global Search</Text>
                        {totalResults > 0 && (
                            <Badge color="blue" size="sm">{totalResults} results</Badge>
                        )}
                    </Group>
                }
                overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
            >
                <Stack gap="lg">
                    {/* Search Input */}
                    <TextInput
                        ref={searchInputRef}
                        placeholder="Search by invoice, batch, student, instructor name, email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="md"
                        leftSection={<IconSearch size={18} />}
                        rightSection={
                            isLoading ? (
                                <Loader size="xs" />
                            ) : searchQuery ? (
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            ) : null
                        }
                    />

                    {/* Filter Buttons */}
                    {searchQuery.trim() !== '' && (
                        <Group gap="xs">
                            <Button
                                variant={activeFilter === 'all' ? 'filled' : 'light'}
                                color="blue"
                                size="xs"
                                onClick={() => setActiveFilter('all')}
                            >
                                All ({totalResults})
                            </Button>
                            <Button
                                variant={activeFilter === 'invoice' ? 'filled' : 'light'}
                                color="yellow"
                                size="xs"
                                leftSection={<IconInvoice size={14} />}
                                onClick={() => setActiveFilter('invoice')}
                            >
                                Invoices ({getFilteredCount('invoice')})
                            </Button>
                            <Button
                                variant={activeFilter === 'batch' ? 'filled' : 'light'}
                                color="red"
                                size="xs"
                                leftSection={<IconSchool size={14} />}
                                onClick={() => setActiveFilter('batch')}
                            >
                                Batches ({getFilteredCount('batch')})
                            </Button>
                            <Button
                                variant={activeFilter === 'student' ? 'filled' : 'light'}
                                color="green"
                                size="xs"
                                leftSection={<IconUsers size={14} />}
                                onClick={() => setActiveFilter('student')}
                            >
                                Students ({getFilteredCount('student')})
                            </Button>
                            <Button
                                variant={activeFilter === 'instructor' ? 'filled' : 'light'}
                                color="purple"
                                size="xs"
                                leftSection={<IconUserCheck size={14} />}
                                onClick={() => setActiveFilter('instructor')}
                            >
                                Instructors ({getFilteredCount('instructor')})
                            </Button>
                        </Group>
                    )}

                    {/* Results */}
                    {searchQuery.trim() !== '' && (
                        <ScrollArea h={500} type="auto">
                            <Stack gap="xl">
                                {shouldShowSection('invoice') && (
                                    <>
                                        {renderSection(
                                            'Invoices',
                                            <IconInvoice size={20} color="#ffc107" />,
                                            searchResults.invoices,
                                            renderInvoiceCard,
                                            'yellow'
                                        )}
                                        {searchResults.invoices.length > 0 && activeFilter === 'all' && <Divider />}
                                    </>
                                )}

                                {shouldShowSection('batch') && (
                                    <>
                                        {renderSection(
                                            'Batches',
                                            <IconSchool size={20} color="#f5576c" />,
                                            searchResults.batches,
                                            renderBatchCard,
                                            'red'
                                        )}
                                        {searchResults.batches.length > 0 && activeFilter === 'all' && <Divider />}
                                    </>
                                )}

                                {shouldShowSection('student') && (
                                    <>
                                        {renderSection(
                                            'Students',
                                            <IconUsers size={20} color="#10b981" />,
                                            searchResults.students,
                                            renderStudentCard,
                                            'green'
                                        )}
                                        {searchResults.students.length > 0 && activeFilter === 'all' && <Divider />}
                                    </>
                                )}

                                {shouldShowSection('instructor') && (
                                    <>
                                        {renderSection(
                                            'Instructors',
                                            <IconUserCheck size={20} color="#a855f7" />,
                                            searchResults.instructors,
                                            renderInstructorCard,
                                            'purple'
                                        )}
                                        {searchResults.instructors.length > 0 && activeFilter === 'all' && <Divider />}
                                    </>
                                )}
                            </Stack>
                        </ScrollArea>
                    )}

                    {searchQuery.trim() === '' && (
                        <Box p="xl">
                            <Text size="sm" c="dimmed" ta="center">
                                Start typing to search across invoices, batches, students, instructors, and attendance records
                            </Text>
                        </Box>
                    )}
                </Stack>
            </Modal>
        </>
    );
}